import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Search, ShieldAlert, ShieldCheck, KeyRound, Clock } from "lucide-react";

const EVENTS = [
  { value: "all", label: "All events" },
  { value: "otp_requested", label: "OTP requested" },
  { value: "password_changed", label: "Password changed" },
  { value: "otp_failed", label: "Wrong code entered" },
  { value: "otp_expired", label: "Code expired" },
  { value: "otp_invalid", label: "Invalid / reused code" },
];

const PURPOSES = {
  change_password: { label: "User change password", color: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10" },
  forgot_password: { label: "Forgot password (self)", color: "text-violet-400 border-violet-500/30 bg-violet-500/10" },
  admin_reset: { label: "Admin-triggered reset", color: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
};

const EVENT_META = {
  otp_requested: { icon: KeyRound, color: "text-cyan-400" },
  password_changed: { icon: ShieldCheck, color: "text-emerald-400" },
  otp_failed: { icon: ShieldAlert, color: "text-rose-400" },
  otp_expired: { icon: Clock, color: "text-zinc-400" },
  otp_invalid: { icon: ShieldAlert, color: "text-rose-400" },
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminPasswordAudit() {
  const [rows, setRows] = useState([]);
  const [event, setEvent] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {};
    if (event !== "all") params.event = event;
    api.get("/admin/password-audit", { params })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [event]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      (r.user_email || "").toLowerCase().includes(s) ||
      (r.requested_by_email || "").toLowerCase().includes(s) ||
      (r.purpose || "").toLowerCase().includes(s) ||
      (r.detail || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight">Password Audit Log</h1>
          <p className="mt-2 text-sm text-zinc-500 max-w-2xl">
            Every password-reset request, code send, wrong attempt and successful change on SonotekDigi.
            Actual verification codes and passwords are <span className="text-emerald-400">never</span> stored in this log.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by user or admin email..."
              data-testid="audit-search"
              className="pl-9 bg-zinc-900 border-zinc-800 w-72" />
          </div>
          <Select value={event} onValueChange={setEvent}>
            <SelectTrigger data-testid="audit-event-filter" className="bg-zinc-900 border-zinc-800 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4">When</th>
              <th className="p-4">Event</th>
              <th className="p-4">User</th>
              <th className="p-4">Purpose</th>
              <th className="p-4">Requested by</th>
              <th className="p-4">Details</th>
            </tr>
          </thead>
          <tbody data-testid="audit-tbody">
            {loading && <tr><td colSpan="6" className="p-12 text-center text-zinc-500">Loading...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="6" className="p-12 text-center text-zinc-500">No password audit entries yet.</td></tr>
            )}
            {!loading && filtered.map((r) => {
              const meta = EVENT_META[r.event] || {};
              const Icon = meta.icon;
              const purpose = PURPOSES[r.purpose] || { label: r.purpose, color: "text-zinc-400 border-zinc-700 bg-zinc-800" };
              return (
                <tr key={r.id} data-testid={`audit-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                  <td className="p-4 text-zinc-400 text-sm font-mono whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="p-4">
                    <div className={`inline-flex items-center gap-1.5 text-sm ${meta.color || "text-zinc-300"}`}>
                      {Icon ? <Icon className="w-4 h-4" /> : null}
                      <span>{r.event}</span>
                      {r.success
                        ? <span className="ml-1 text-[10px] uppercase tracking-wider text-emerald-400/80">success</span>
                        : <span className="ml-1 text-[10px] uppercase tracking-wider text-rose-400/80">failed</span>}
                    </div>
                  </td>
                  <td className="p-4 text-zinc-300 text-sm">{r.user_email}</td>
                  <td className="p-4">
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs border ${purpose.color}`}>{purpose.label}</span>
                  </td>
                  <td className="p-4 text-zinc-300 text-sm">
                    <div>{r.requested_by_email}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{r.requested_by_role}</div>
                  </td>
                  <td className="p-4 text-zinc-400 text-xs max-w-sm">{r.detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
